import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';

const MobileHamburgerMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { ensName, getAuthHeaders, isAuthenticated } = useContext(AuthContext);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchUnread = async () => {
      try {
        const headers = getAuthHeaders();
        const res = await fetch('/api/zpost/notifications/unread-count', { headers });
        const data = await res.json();
        if (data.success) setUnreadCount(data.count);
      } catch (err) {
        console.error('Failed to fetch unread count', err);
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, getAuthHeaders]);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const handleZhatClick = (e) => {
    e.preventDefault();
    closeMenu();
    const zhatUrl = ensName ? `/zhat?ens=${encodeURIComponent(ensName)}` : '/zhat';
    window.open(zhatUrl, '_blank', 'noopener,noreferrer');
  };

  const handleBankSystemClick = (e) => {
    e.preventDefault();
    closeMenu();
    window.open('https://banksystem.co/', '_blank', 'noopener,noreferrer');
  };

  const handleNavigation = (path) => {
    closeMenu();
    navigate(path);
  };

  return (
    <>
      {/* Hamburger Button */}
      <button
        onClick={toggleMenu}
        style={{
          position: 'fixed',
          top: '1rem',
          left: '1rem',
          zIndex: 1001,
          background: 'var(--gold)',
          border: 'none',
          borderRadius: '8px',
          width: '44px',
          height: '44px',
          fontSize: '1.5rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--black)',
          boxShadow: '0 2px 8px rgba(255,215,0,0.3)',
        }}
        aria-label="Menu"
      >
        {isOpen ? '✕' : '☰'}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(4px)',
            zIndex: 1002,
            animation: 'fadeIn 0.2s ease',
          }}
          onClick={closeMenu}
        />
      )}

      {/* Menu Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: '280px',
          maxWidth: '80vw',
          backgroundColor: 'var(--black)',
          borderRight: '2px solid var(--gold)',
          boxShadow: '5px 0 40px rgba(0,0,0,0.5)',
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          zIndex: 1003,
          display: 'flex',
          flexDirection: 'column',
          padding: '2rem 1rem 1rem 1rem',
          overflowY: 'auto',
        }}
      >
        {/* Close button inside drawer */}
        <button
          onClick={closeMenu}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'transparent',
            border: 'none',
            color: 'var(--gold)',
            fontSize: '1.5rem',
            cursor: 'pointer',
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Logo */}
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <span className="gold-logo" style={{ fontSize: '1.8rem' }}>Zpost</span>
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            Your voice, your rules
          </div>
        </div>

        {/* Navigation Links */}
        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {/* Home */}
          <button
            onClick={() => handleNavigation('/home')}
            className="sidebar-nav-item"
            style={{ background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
          >
            <i className="fas fa-home" style={{ width: '24px' }}></i>
            <span>Home</span>
          </button>

          {/* Zhat (external) */}
          <button
            onClick={handleZhatClick}
            className="sidebar-nav-item"
            style={{ background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
          >
            <i className="fas fa-comments" style={{ width: '24px' }}></i>
            <span>Zhat</span>
            <i className="fas fa-external-link-alt" style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--gold)' }}></i>
          </button>

          {/* Zearch */}
          <button
            onClick={() => handleNavigation('/zearch')}
            className="sidebar-nav-item"
            style={{ background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
          >
            <i className="fas fa-search" style={{ width: '24px' }}></i>
            <span>Zearch</span>
          </button>

          {/* Zwallet */}
          <button
            onClick={() => handleNavigation('/home/wallet')}
            className="sidebar-nav-item"
            style={{ background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
          >
            <i className="fas fa-wallet" style={{ width: '24px' }}></i>
            <span>Zwallet</span>
          </button>

          {/* Identity */}
          <button
            onClick={() => handleNavigation('/home/identity')}
            className="sidebar-nav-item"
            style={{ background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
          >
            <i className="fas fa-id-card" style={{ width: '24px' }}></i>
            <span>Identity</span>
          </button>

          {/* Profile */}
          <button
            onClick={() => handleNavigation('/profile')}
            className="sidebar-nav-item"
            style={{ background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
          >
            <i className="fas fa-user" style={{ width: '24px' }}></i>
            <span>Profile</span>
          </button>

          {/* Notifications (with badge) */}
          <button
            onClick={() => handleNavigation('/notices')}
            className="sidebar-nav-item"
            style={{ background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <i className="fas fa-bell" style={{ width: '24px' }}></i>
            <span>Notices</span>
            {unreadCount > 0 && (
              <span style={{
                marginLeft: 'auto',
                background: 'var(--gold)',
                color: '#000',
                borderRadius: '10px',
                padding: '2px 8px',
                fontSize: '0.8rem'
              }}>
                {unreadCount}
              </span>
            )}
          </button>

          {/* Settings (formerly Help Center) */}
          <button
            onClick={() => handleNavigation('/help')}
            className="sidebar-nav-item"
            style={{ background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
          >
            <i className="fas fa-cog" style={{ width: '24px' }}></i>
            <span>Settings</span>
          </button>

          {/* BankSystem (external) */}
          <button
            onClick={handleBankSystemClick}
            className="sidebar-nav-item"
            style={{ background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}
          >
            <i className="fas fa-university" style={{ width: '24px' }}></i>
            <span>BankSystem</span>
            <i className="fas fa-external-link-alt" style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--gold)' }}></i>
          </button>
        </nav>

        {/* Footer */}
        <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--border)', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.7rem' }}>
          Powered by .zpost.eth
        </div>
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default MobileHamburgerMenu;
